<template>
  <article class="StepCard">
    <header class="StepCard-header">
      <span class="StepCard-badge">
        <Icon
          :icon="icon"
          :color="iconColor"
          size="lg"
        />
      </span>

      <span class="StepCard-number" aria-hidden="true">{{ stepNumber }}</span>
    </header>

    <div class="StepCard-content">
      <span v-if="label" class="StepCard-label">{{ label }}</span>

      <component
        :is="titleAs ?? 'h3'"
        class="StepCard-title"
      >
        {{ title }}
      </component>

      <p v-if="description" class="StepCard-description">
        {{ description }}
      </p>
    </div>
  </article>
</template>

<script setup lang="ts">
import Icon from '@cvo/ui/components/atoms/Icon/Icon.vue';
import type { StepCardProps } from './StepCard.model';

defineProps<StepCardProps>();
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/responsive';
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  // CVO-19494: hand-rolled card surface. Could consume the Card atom instead,
  // once its padding/bg/radius/border are overridable.
  .StepCard {
    // Keeps the card above the connector line drawn by the parent section,
    // so the line shows only in the gaps between cards
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: vars.$dimensions-d-5;
    padding: vars.$dimensions-d-4;
    background-color: vars.$organisms-card-steps-card-bg-color;
    border: vars.$dimensions-d-0-25 solid vars.$organisms-card-steps-card-border-color;
    border-radius: vars.$dimensions-d-3;
    // Clips the oversized background figure
    overflow: hidden;

    @include responsive.tablet {
      gap: vars.$dimensions-d-0;
      padding: vars.$dimensions-d-8;
      border-radius: vars.$dimensions-d-4;
    }

    &-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    &-badge {
      // Figma sizes this container at 50, which is off the 4px dimensions scale
      @include layout.square(50px);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      color: vars.$organisms-card-contentcard-steps-icon-color;
      background-color: vars.$organisms-card-contentcard-steps-icon-bg-color;
      border-radius: vars.$dimensions-d-3;

      @include responsive.tablet {
        @include layout.square(vars.$dimensions-d-14);
      }
    }

    &-number {
      // h1 at an explicit line height: `normal` would render the line box a pixel taller than
      // the 56px the header hugs to in Figma.
      @include text.heading-base(vars.$font-size-h1, vars.$dimensions-d-14, 600);
      color: vars.$organisms-card-contentcard-steps-number-color;
      // Figma renders the background figure at 8%
      opacity: 0.08;
      text-align: end;
      user-select: none;

      // The oversized figure is a design motif, but only from tablet up: on mobile its line box
      // alone stands taller than the 56px the header is meant to hug.
      @include responsive.tablet {
        @include text.heading-base(vars.$font-size-display, normal, 600);
      }
    }

    &-content {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-4;

      @include responsive.tablet {
        gap: vars.$dimensions-d-3-5;
      }
    }

    &-label {
      @include text.text('sm');
      color: vars.$organisms-card-contentcard-steps-step-color;
    }

    &-title {
      margin: 0;
      color: vars.$organisms-card-contentcard-steps-title-color;

      @include text.heading-responsive('h5', 'h4', 'h4');
    }

    &-description {
      margin: 0;
      color: vars.$organisms-card-contentcard-steps-text-color;

      @include text.text-responsive('sm', 'md', 'md');
    }
  }
}
</style>
